import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { TimezoneCombobox } from '@/components/timezone-combobox'
import { Button } from '@/components/ui/button'
import { Field, FormError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { notifyError } from '@/components/notify'
import { useCreateTenant } from '@/features/auth/api'
import { applyServerErrors } from '@/lib/form-errors'
import { browserTimeZone } from '@/lib/timezones'
import type { Tenant } from '@/types/api'

const schema = z.object({
  name: z.string().trim().min(2, 'Use at least 2 characters').max(120, 'Keep it under 120 characters'),
  timezone: z.string().min(1, 'Choose a timezone'),
})
type Values = z.infer<typeof schema>

export function OrganizationForm({ onCreated, submitLabel = 'Create organization', autoFocus }: { onCreated: (tenant: Tenant) => void; submitLabel?: string; autoFocus?: boolean }) {
  const create = useCreateTenant()
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { name: '', timezone: browserTimeZone() } })
  const { errors } = form.formState

  const onSubmit = form.handleSubmit((values) =>
    create.mutateAsync(values).then(onCreated, (error) => {
      if (!applyServerErrors(error, form.setError, { fields: ['name', 'timezone'] })) notifyError(error)
    }),
  )

  return (
    <form onSubmit={onSubmit} className="grid gap-4" noValidate>
      <FormError message={errors.root?.server?.message} />
      <Field label="Organization name" error={errors.name?.message} hint="Usually your company or brand name. You can change it later.">
        <Input placeholder="Acme Retail" autoComplete="organization" autoFocus={autoFocus} {...form.register('name')} />
      </Field>
      <Field label="Timezone" error={errors.timezone?.message} hint="Default for new locations, schedules and reports.">
        <Controller control={form.control} name="timezone" render={({ field }) => <TimezoneCombobox value={field.value} onChange={field.onChange} />} />
      </Field>
      <Button type="submit" variant="brand" size="lg" loading={create.isPending} className="mt-1">
        {submitLabel}
      </Button>
    </form>
  )
}
